import { Alert, Button, Drawer, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import DashboardCard from '/@/components/bayer/dashboard-card';
import ModalV2 from '/@/components/bayer/modal-v2';
import { FinalizeCampaignModal } from '/@/components/crop-monitoring/harvest-alert';
import { usePlantingReportResult } from '/@/components/field-list-v2/components/sidebar/hooks';
import Icon from '/@/components/icon';
import PlantingReportInfoCard from '/@/components/planting-report-info-card';
import useUnitProvider from '/@/core/context/unit';
import { useCornCropMonitoringAlertConditions } from '/@/core/crop-monitoring';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { useMutateArchiveCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useGetUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getCatalogBrand } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import { formatDate } from '/@/utils';
import { cropMonitoringPendingAsyncState } from '/@/utils/crop-monitoring';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import NextVisitModal from '/@/views/dashboard/grower/components/next-visit-modal';
import YieldEstimationGuideModal from '/@/views/fields/field-monitoring/components/yield-estimation-guide-modal';
import CreateReportModal from '../create-report-modal';
import CropMonitoringDialog from '../creation-dialog';
import FieldCurrentStatus from '../current-status';
import YieldEstimationForm from '../yield-estimation-form';
import StageInfoMessage from './components/stage-info-message';
import Registries from './registries';
import AddRegistryModal from './registries/components/add';
import type { RegistryAddStep } from './registries/components/add/types';

const FieldMonitoringDetail = ({
    fieldId,
    cropMonitoring,
    showCropProtection,
    setShowCropProtection,
    setTechnology,
    technology,
    showAlert,
    historicalWeather,
    loadingWeather,
    references,
}: {
    fieldId: number;
    cropMonitoring?: CropMonitoring;
    showCropProtection: boolean;
    setShowCropProtection: (show: boolean) => void;
    setTechnology: (technology: string) => void;
    technology: string;
    showAlert: boolean;
    historicalWeather: HistoricalWeather;
    loadingWeather: boolean;
    references: { text: string; color: string }[];
}) => {
    const units = useUnitProvider();
    const fieldQuery = useFieldById({ id: fieldId });
    const isMobile = useIsMobile();
    const country = useSelector(getSessionCountry);
    const cropProtectionBrand = useSelector((state) => getCatalogBrand(state, 'crop_protection'));
    const cropProtectionProductsQuery = useAllCatalogProducts({
        brand: cropProtectionBrand?.name,
        crop: 'crop_protection',
        country: country!.code!,
        cropProtectionCrop: undefined,
        cropProtectionProductType: undefined,
    });
    const cropProtectionProductsEnabled = cropProtectionBrand && cropProtectionProductsQuery.data?.results?.length > 0;

    const [yieldEstimationDrawerIsOpen, setYieldEstimationDrawerIsOpen] = useState(false);
    const [yieldEstimationStep, setYieldEstimationStep] = useState<'form' | 'result'>('form');
    const [yieldEstimationId, setYieldEstimationId] = useState<number | null>(null);
    const [yieldEstimationDirty, setYieldEstimationDirty] = useState(false);
    const [closeModalIsOpen, setCloseModalIsOpen] = useState(false);
    const [CMFormModalIsOpen, setCMFormModalIsOpen] = useState(false);
    const [createReportModalIsOpen, setCreateReportModalIsOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addModalInitialStep, setAddModalInitialStep] = useState<RegistryAddStep>('select');
    const [nextVisitModalOpen, setNextVisitModalOpen] = useState(false);
    const [guideModalIsOpen, setGuideModalIsOpen] = useState(false);
    const [finalizeCampaignModalIsOpen, setFinalizeCampaignModalIsOpen] = useState(false);
    const [harvestReportCropMonitoring, setHarvestReportCropMonitoring] = useState<CropMonitoring | undefined>(
        undefined
    );
    const navigate = useNavigate();
    const grower = useGetUserNode(fieldQuery.data?.userNode ?? 0);
    const today = new Date();
    const plantingDate = cropMonitoring ? new Date(cropMonitoring.plantingDate) : null;
    const theme = useTheme();

    // Corn crop-monitoring created but not processed yet: exists, planting date passed, but
    // the backend GDU/phenology job hasn't run (`accumulatedGduDate` unset). Keyed on that
    // rather than on `phenology`, which the job can legitimately leave unset for a CM
    // processed right after planting — that would pin the state on forever. Drives both the
    // "preparando los datos" Alert and hiding the stage-info message (which has nothing
    // meaningful to show until then). Kept in sync with `FieldCurrentStatus`.
    const isCornPhenologyProcessing =
        cropMonitoring?.crop === 'corn' &&
        cropMonitoringPendingAsyncState(cropMonitoring) &&
        plantingDate != null &&
        today.getTime() > plantingDate.getTime();

    const title = useMemo(() => {
        const options = {
            corn: _('Híbrido sembrado'),
        };
        return options[cropMonitoring?.crop as keyof typeof options] || _('Variedad sembrada'); // for wheat and soy
    }, [cropMonitoring?.crop]);

    const canEditResource = useHasWriteAccess();
    const archiveCropMonitoringByIdMutation = useMutateArchiveCropMonitoringById(cropMonitoring?.id);
    const recentPlantingResult = usePlantingReportResult(fieldQuery.data);
    const alertConditions = useCornCropMonitoringAlertConditions({ cropMonitoring });

    const openHarvestReport = useCallback(
        (targetCropMonitoring?: CropMonitoring) => {
            // Defaults to the field's active crop monitoring — Barbecho's "Obtener reporte"
            // passes the previous (closed) campaign explicitly instead, since there may be no
            // active one at all.
            setHarvestReportCropMonitoring(targetCropMonitoring ?? cropMonitoring);
            setCreateReportModalIsOpen(true);
        },
        [cropMonitoring]
    );

    const openAddModal = useCallback((initialStep: RegistryAddStep = 'select') => {
        setAddModalInitialStep(initialStep);
        setAddModalOpen(true);
    }, []);

    const openYieldEstimation = useCallback((step: 'form' | 'result' = 'form', estimationId: number | null = null) => {
        setYieldEstimationStep(step);
        setYieldEstimationId(estimationId);
        setYieldEstimationDrawerIsOpen(true);
    }, []);

    // Confirm before closing only when the yield-estimation form has unsaved changes.
    const requestCloseYieldEstimation = useCallback(() => {
        if (yieldEstimationDirty) {
            setCloseModalIsOpen(true);
        } else {
            setYieldEstimationDrawerIsOpen(false);
        }
    }, [yieldEstimationDirty]);

    const openRecommendHerbicide = useCallback(() => {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'field-detail-fallow',
            crop: cropMonitoring?.crop,
            cp_type: 'weed',
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId,
                    crop: cropMonitoring?.crop,
                    type: 'weeds',
                    plantingDate: cropMonitoring?.plantingDate,
                    userNodeId: fieldQuery.data?.userNode,
                },
            })
        );
    }, [navigate, fieldId, cropMonitoring?.crop, cropMonitoring?.plantingDate, fieldQuery.data?.userNode]);

    const openCreateVisitReport = useCallback(() => {
        navigate(reverse('visitReport.VisitReportEdit', { qs: { fieldIds: String(fieldId) } }));
    }, [navigate, fieldId]);

    const openRequestNSmart = useCallback(() => {
        mpTrackEvent('N-Smart Recommendation began', { entry_source: 'crop-monitoring-stage-alert' });
        navigate(
            reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                qs: { cropMonitoringId: cropMonitoring?.id },
            })
        );
    }, [navigate, cropMonitoring?.id]);

    const openGenerateRecommendation = useCallback(() => {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'crop-monitoring-stage-alert',
            crop: cropMonitoring?.crop,
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId,
                    crop: cropMonitoring?.crop,
                    plantingDate: cropMonitoring?.plantingDate,
                    userNodeId: fieldQuery.data?.userNode,
                },
            })
        );
    }, [navigate, fieldId, cropMonitoring?.crop, cropMonitoring?.plantingDate, fieldQuery.data?.userNode]);

    const openConfirmNSmartDate = useCallback(() => {
        navigate(
            reverse('nitrogenRecommendation.NitrogenRecommendationDetail', {
                params: { id: cropMonitoring?.nitrogenRecomendations?.[0] as number | undefined },
            })
        );
    }, [navigate, cropMonitoring?.nitrogenRecomendations]);

    const openRecommendFungicide = useCallback(() => {
        const trackedCrop =
            cropMonitoring?.crop === 'corn' || cropMonitoring?.crop === 'soy' ? cropMonitoring.crop : undefined;
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'crop-monitoring-stage-alert',
            crop: trackedCrop,
            cp_type: 'disease',
        });
        navigate(
            reverse('cropProtectionRecommendation', {
                qs: {
                    fieldId,
                    crop: cropMonitoring?.crop,
                    type: 'diseases',
                    plantingDate: cropMonitoring?.plantingDate,
                    userNodeId: fieldQuery.data?.userNode,
                },
            })
        );
    }, [navigate, fieldId, cropMonitoring?.crop, cropMonitoring?.plantingDate, fieldQuery.data?.userNode]);

    return (
        <>
            {fieldQuery.data && !isCornPhenologyProcessing && (
                <StageInfoMessage
                    cropMonitoring={cropMonitoring}
                    field={fieldQuery.data}
                    onOpenHarvestReport={openHarvestReport}
                    onRegisterApplication={() => openAddModal('application')}
                    onRecommendHerbicide={openRecommendHerbicide}
                    onCreateVisitReport={openCreateVisitReport}
                    onScheduleVisit={() => setNextVisitModalOpen(true)}
                    onRequestNSmart={openRequestNSmart}
                    onGenerateRecommendation={openGenerateRecommendation}
                    onConfirmNSmartDate={openConfirmNSmartDate}
                    onRecommendFungicide={openRecommendFungicide}
                    onViewSamplingGuide={() => setGuideModalIsOpen(true)}
                    onEstimateYield={() => openYieldEstimation('form')}
                    onFinalizeCampaignWithoutData={() => setFinalizeCampaignModalIsOpen(true)}
                    onArchiveCropMonitoring={() => archiveCropMonitoringByIdMutation.trigger()}
                />
            )}
            <Grid2 container direction='row' spacing={3} mx={'-12px !important'}>
                <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                    {cropMonitoring?.active ? (
                        <DashboardCard
                            data-testid='field-monitoring-detail-active-crop-card'
                            title={title}
                            number={cropMonitoring.productName}
                            subtitle={
                                cropMonitoring?.result?.plantedArea
                                    ? `${_('Superficie sembrada')}: ${convertAreaFromM2(cropMonitoring.result.plantedArea, units).toString()}`
                                    : undefined
                            }
                        />
                    ) : (
                        <DashboardCard
                            title={_('Próximo cultivo')}
                            data-testid='field-monitoring-detail-next-crop-card'
                            subtitle={_('Agregá el próximo cultivo para poder hacer un mejor seguimiento')}
                            buttonProps={
                                recentPlantingResult
                                    ? undefined
                                    : {
                                          onClick: () => setCMFormModalIsOpen(true),
                                          startIcon: (
                                              <Icon
                                                  type='agronomy'
                                                  size='xxs'
                                                  sx={{
                                                      fill: canEditResource
                                                          ? theme.palette.common.white
                                                          : theme.palette.action.disabled,
                                                  }}
                                                  padding={false}
                                              />
                                          ),
                                          children: _('Agregar cultivo'),
                                          size: 'small',
                                          disabled: !canEditResource,
                                      }
                            }
                        >
                            {recentPlantingResult && (
                                <PlantingReportInfoCard
                                    recentPlantingResult={recentPlantingResult}
                                    actions={
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            startIcon={
                                                <Icon
                                                    type='agronomy'
                                                    size='xxs'
                                                    sx={{ fill: theme.palette.common.white }}
                                                    padding={false}
                                                />
                                            }
                                            onClick={() => setCMFormModalIsOpen(true)}
                                        >
                                            {_('Agregar próximo cultivo')}
                                        </Button>
                                    }
                                />
                            )}
                        </DashboardCard>
                    )}
                </Grid2>
                <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                    {cropMonitoring?.harvestDate ? (
                        <DashboardCard
                            title={_('Fecha de cosecha')}
                            number={
                                cropMonitoring?.harvestDate
                                    ? formatDate(new Date(cropMonitoring.harvestDate))
                                    : undefined
                            }
                        />
                    ) : (
                        <DashboardCard
                            title={_('Fecha de siembra')}
                            number={cropMonitoring ? formatDate(new Date(cropMonitoring.plantingDate)) : undefined}
                            subtitle={
                                cropMonitoring
                                    ? undefined
                                    : _('Acá vas a ver la fecha de siembra una vez que comience la campaña')
                            }
                        />
                    )}
                </Grid2>
                <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                    <DashboardCard
                        title={_('Densidad')}
                        data-testid='field-monitoring-detail-density-card'
                        number={
                            !cropMonitoring
                                ? undefined
                                : cropMonitoring?.density
                                  ? units.toDensity(cropMonitoring.density || 0, cropMonitoring.crop, 2).toString()
                                  : '-'
                        }
                        subtitle={
                            cropMonitoring ? undefined : _('Acá vas a ver la densidad una vez que comience la siembra')
                        }
                    />
                </Grid2>
                <Grid2 xs={12} md={3} sx={{ height: 'auto' }}>
                    <DashboardCard
                        title={_('Ambiente')}
                        data-testid='field-monitoring-detail-environment-card'
                        number={
                            cropMonitoring
                                ? units.toYield(cropMonitoring.environment, cropMonitoring.crop, 2).toString()
                                : undefined
                        }
                        subtitle={
                            cropMonitoring ? undefined : _('Acá vas a ver el ambiente una vez que comience la campaña')
                        }
                    />
                </Grid2>
            </Grid2>

            {isCornPhenologyProcessing && (
                <Alert severity='info' sx={{ my: 4 }}>
                    {_('Estamos preparando los datos para que sigas el avance del cultivo en cada etapa.')}
                </Alert>
            )}

            <Grid2 container spacing={3} sx={{ mt: 3 }} alignItems='stretch'>
                <Grid2 xs={12} md={8}>
                    <Registries
                        fieldId={fieldId}
                        cropMonitoring={cropMonitoring}
                        onOpenAddModal={openAddModal}
                        onOpenYieldEstimation={openYieldEstimation}
                    />
                </Grid2>
                <Grid2 xs={12} md={4}>
                    <Stack spacing={2}>
                        <FieldCurrentStatus
                            cropMonitoring={cropMonitoring}
                            technology={technology}
                            showCropProtection={showCropProtection}
                            showAlert={showAlert}
                            historicalWeather={historicalWeather}
                            loadingWeather={loadingWeather}
                        />
                    </Stack>
                </Grid2>
            </Grid2>

            {cropMonitoring && (
                <Drawer open={yieldEstimationDrawerIsOpen} onClose={requestCloseYieldEstimation} anchor='right'>
                    <YieldEstimationForm
                        cropMonitoring={cropMonitoring}
                        setYieldEstimationDrawerIsOpen={setYieldEstimationDrawerIsOpen}
                        onRequestClose={requestCloseYieldEstimation}
                        onDirtyChange={setYieldEstimationDirty}
                        open={yieldEstimationDrawerIsOpen}
                        initialStep={yieldEstimationStep}
                        estimationId={yieldEstimationId}
                    />
                </Drawer>
            )}
            <ModalV2
                open={closeModalIsOpen}
                onClose={() => {
                    setCloseModalIsOpen(false);
                }}
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setCloseModalIsOpen(false);
                            }}
                        >
                            {_('Seguir editando')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => {
                                setCloseModalIsOpen(false);
                                setYieldEstimationDrawerIsOpen(false);
                            }}
                        >
                            {_('Confirmar')}
                        </Button>
                    </>
                }
                title={_('¿Querés salir sin guardar?')}
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'sm'}
            >
                <Typography color={'primary'} variant='body1'>
                    {_('Al cancelar, los datos ingresados no se guardarán y tendrás que volver a cargarlos.')}
                </Typography>
            </ModalV2>
            {harvestReportCropMonitoring && fieldQuery.data && (
                <CreateReportModal
                    field={fieldQuery.data}
                    cropMonitoring={harvestReportCropMonitoring}
                    isOpen={createReportModalIsOpen}
                    setIsOpen={setCreateReportModalIsOpen}
                />
            )}
            {fieldQuery.data && (
                <CropMonitoringDialog
                    open={CMFormModalIsOpen}
                    setClose={() => setCMFormModalIsOpen(false)}
                    field={fieldQuery.data}
                    entry_source='field-detail'
                    plantingReport={recentPlantingResult ?? undefined}
                />
            )}
            <AddRegistryModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                fieldId={fieldId}
                cropMonitoring={cropMonitoring}
                initialStep={addModalInitialStep}
                onHarvest={openHarvestReport}
                onYieldEstimation={() => openYieldEstimation('form')}
            />
            <NextVisitModal
                open={nextVisitModalOpen}
                onClose={() => setNextVisitModalOpen(false)}
                growerName={grower.data?.name ?? ''}
                growerPhone={grower.data?.phone}
                growerId={fieldQuery.data?.userNode}
            />
            <YieldEstimationGuideModal open={guideModalIsOpen} onClose={() => setGuideModalIsOpen(false)} />
            {cropMonitoring && (
                <FinalizeCampaignModal
                    cropMonitoring={cropMonitoring}
                    open={finalizeCampaignModalIsOpen}
                    onClose={() => setFinalizeCampaignModalIsOpen(false)}
                    setOpenHarvestModal={openHarvestReport}
                    setCloseHarvestModal={() => setFinalizeCampaignModalIsOpen(false)}
                />
            )}
        </>
    );
};

export default FieldMonitoringDetail;
